import { AnalyticsCore } from "../core.js";

export class WebAnalytics extends AnalyticsCore {
  constructor(config = {}) {
    super(config);
    this.apiEndpoint = "https://app.mighty45.com/api/v1";
    this.setupWebSpecificFeatures();
  }

  setupWebSpecificFeatures() {
    this.setupNavigationTracking();
    this.setupPerformanceTracking();
    this.setupBatchProcessing();
  }

  setupNavigationTracking() {
    // Handle browser navigation events
    window.addEventListener("beforeunload", () => {
      this.flush();
    });

    // Handle visibility changes
    document.addEventListener("visibilitychange", () => {
      if (document.hidden) {
        this.track("APP_BACKGROUNDED");
      } else {
        this.track("APP_FOREGROUND");
      }
    });
  }

  setupPerformanceTracking() {
    if ("performance" in window) {
      this.trackPageLoad();
    }
  }

  setupBatchProcessing() {
    // Auto-flush queue periodically
    setInterval(() => {
      if (this.eventQueue.length > 0) {
        this.flush();
      }
    }, this.config.BATCH_TIMEOUT);

    // Auto-flush when queue gets too large
    const originalTrack = this.track.bind(this);
    this.track = (eventName, properties) => {
      const result = originalTrack(eventName, properties);
      if (this.eventQueue.length >= this.config.MAX_EVENTS_PER_BATCH) {
        this.flush();
      }
      return result;
    };
  }

  trackPageLoad() {
    window.addEventListener("load", () => {
      setTimeout(() => {
        const timing = performance.timing;
        const loadTime = timing.loadEventEnd - timing.navigationStart;
        if (loadTime > 0) {
          this.trackPerformance("page_load_time", loadTime, {
            page_url: window.location.href,
            user_agent: navigator.userAgent,
          });
        }
      }, 100);
    });
  }

  async sendBatch(events) {
    if (!events || events.length === 0) return true;

    try {
      // Ensure events have correct platform and ingestion source
      const formattedEvents = events.map(event => ({
        ...event,
        platform: "web",
        ingestion_source: "web_app"
      }));

      const response = await fetch(`${this.apiEndpoint}/analytics/ingest-batch`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({ events: formattedEvents }),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      return true;
    } catch (error) {
      console.error("Analytics batch send failed:", error);
      if (this.config.DEBUG_MODE) {
        console.error("Failed events:", events);
      }
      return false;
    }
  }

  async sendSingle(event) {
    if (!event) return true;

    try {
      // Ensure event has correct platform and ingestion source
      const formattedEvent = {
        ...event,
        platform: "web",
        ingestion_source: "web_app"
      };

      const response = await fetch(`${this.apiEndpoint}/analytics/ingest`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(formattedEvent),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      return true;
    } catch (error) {
      console.error("Analytics single send failed:", error);
      return false;
    }
  }

  async flush() {
    const events = this.getQueuedEvents();
    if (events.length === 0) return;

    const success = await this.sendBatch(events);
    if (success) {
      this.clearQueue();
    }
  }
}
